import type { ApiCountry } from '@mfw/common/ui/dtos';

import { config as translationsConfig } from '@mfw/translations';
import { isCivilDate, isDefined, pickPrimary, toCivilDate } from '@mfw/common';
import { isValid } from 'date-fns';

import type {
  ApiUserFitnessProfile,
  UiAuthorUser,
  ApiAuthUser,
  UiAuthUser,
  ApiAuthor,
  ApiUser,
  UiUser,
} from '../../schemas/users.schema';
import type { ApiUserUpdateArgs, UiUserUpdateArgs } from '../types/user.types';
import type { ApiTranslation } from '../../schemas/translations.schema';
import type { UiPermission } from '../../schemas/permissions';
import type { ApiTag } from '../../schemas/tags.schema';
import type { Maybe } from '../../types/maybe.types';

import { mapOrganizationFromApi } from './organizations.mappers';
import { mergeDefaultSocialMedia } from './socialMedia.mappers';
import { Permissions } from '../../schemas/permissions';

export const mapValue = <T>(value: Maybe<T>) =>
  value !== undefined ? value : undefined;

export function mapUserAddressCountryFromApi(country: ApiCountry) {
  const mappedCountry = mapValue(country);
  return mappedCountry
    ? ([{ value: mappedCountry }] as [{ value: ApiCountry }])
    : ([{ value: translationsConfig.defaultCountryCode as ApiCountry }] as [
        { value: ApiCountry },
      ]);
}

export function mapUserRolesFromApi(roles: ApiUser['roles']): UiUser['roles'] {
  return (
    roles?.map((role) => ({
      id: role.id,
      translations:
        role?.translations?.map((translation) => ({
          locale: [{ label: translation.locale, value: translation.locale }],
          name: translation.name,
        })) ?? [],
    })) ?? []
  );
}

export function mapOrganizationUsersFromApi(
  organizationUsers: Maybe<ApiUser['organizationUsers']>,
): UiUser['organizationUsers'] {
  return (
    organizationUsers?.map((organizationUser) => ({
      ...organizationUser,
      organization: organizationUser.organization
        ? {
            id: organizationUser.organization.id,
            logoUrl: mapValue(organizationUser.organization.logoUrl),
            name: organizationUser.organization.name,
          }
        : undefined,
    })) ?? []
  );
}

export function mapFitnessProfileFromApi(
  fitnessProfile: Maybe<ApiUser['fitnessProfiles']>,
): Maybe<UiUser['fitnessProfile']> {
  const latestFitnessProfile = fitnessProfile?.[0];
  if (!latestFitnessProfile) return;
  return {
    id: latestFitnessProfile.id,
    bmi: mapValue(latestFitnessProfile?.bmi),
    bodyFat: mapValue(latestFitnessProfile?.bodyFat),
    bodyFatPercentage: mapValue(latestFitnessProfile?.bodyFatPercentage),
    fitnessGoals: mapFitnessGoalsFromApi(latestFitnessProfile?.fitnessGoals),
    fitnessLevel: mapValue(latestFitnessProfile?.fitnessLevel),
    healthIssues: mapHealthIssuesFromApi(latestFitnessProfile?.healthIssues),
    heart: mapValue(latestFitnessProfile?.heart),
    height: mapValue(latestFitnessProfile?.height),
    maxHeartRate: mapValue(latestFitnessProfile?.maxHeartRate),
    muscleMass: mapValue(latestFitnessProfile?.muscleMass),
    muscleMassPercentage: mapValue(latestFitnessProfile?.muscleMassPercentage),
    weight: mapValue(latestFitnessProfile?.weight),
  };
}

export function mapHealthIssuesFromApi(
  healthIssues: ApiUserFitnessProfile['healthIssues'],
): Maybe<{ checked: boolean; value: string }[]> {
  if (!healthIssues?.length) return [];
  return (healthIssues.filter(Boolean) as ApiTag[]).map((issue) => ({
    checked: true,
    value: issue.id,
  }));
}

export function mapFitnessGoalsFromApi(
  fitnessGoals: ApiUserFitnessProfile['fitnessGoals'],
): Maybe<
  { translations: ApiTranslation[]; checked: boolean; value: string }[]
> {
  if (!fitnessGoals?.length) return [];
  return (fitnessGoals.filter(Boolean) as Partial<ApiTag>[]).map((goal) => ({
    checked: true,
    translations: goal.translations ?? [],
    value: goal.id ?? '',
  }));
}

export function mapUserSettingsFromApi(
  userSettings: Maybe<ApiUser['settings']>,
): Maybe<UiUser['settings']> {
  return userSettings
    ? {
        id: userSettings.id,
        bodyMassIndex: mapValue(userSettings?.bodyMassIndex),
        // Kept as a civil 'yyyy-MM-dd' string in the UI domain; a legacy
        // cached instant normalizes to the browser-local day once here.
        dateOfBirth: isCivilDate(userSettings?.dateOfBirth)
          ? userSettings.dateOfBirth
          : userSettings?.dateOfBirth &&
              isValid(new Date(userSettings?.dateOfBirth))
            ? toCivilDate(new Date(userSettings?.dateOfBirth))
            : undefined,
        energy: mapValue(userSettings?.energy),
        gender: mapValue(userSettings?.gender),
        height: mapValue(userSettings?.height),
        isPublic: mapValue(userSettings?.isPublic),
        length: mapValue(userSettings?.length),
        locale:
          mapValue(userSettings?.locale) ?? translationsConfig.defaultLocale,
        // What the account itself holds: `locale` above reads en-US for an account
        // with none, and such an account takes its tenant's language instead.
        ownLocale: mapValue(userSettings?.locale) ?? null,
        privacyPolicyAccepted: mapValue(userSettings?.privacyPolicyAccepted),
        profileUrl: mapValue(userSettings?.profileUrl),
        rememberMe: mapValue(userSettings?.rememberMe),
        // Both zones reach the UI: `timezone` is the account's choice and
        // `detectedTimezone` is what the server falls back to, so a surface
        // that renders "the timezone" can show the effective one instead of
        // going blank whenever nobody opened the Personal Details form.
        detectedTimezone: mapValue(userSettings?.detectedTimezone),
        timezone: mapValue(userSettings?.timezone),
        volume: mapValue(userSettings?.volume),
        weight: mapValue(userSettings?.weight),
        dailyActivityReminderMorningTime: mapValue(
          userSettings?.dailyActivityReminderMorningTime,
        ),
        dailyActivityReminderEveningTime: mapValue(
          userSettings?.dailyActivityReminderEveningTime,
        ),
      }
    : undefined;
}

export function createUpdateUserArgs(
  args: UiUserUpdateArgs,
): ApiUserUpdateArgs {
  const mappedFitnessLevel = mapValue(args.fitnessProfile?.fitnessLevel);
  const fitnessLevel = mappedFitnessLevel
    ? parseInt(mappedFitnessLevel, 10)
    : undefined;
  return {
    address: args?.address
      ? {
          city: mapValue(args.address?.city),
          country: mapValue(args.address?.country?.[0]?.value),
          isAddressPublic: mapValue(args.address?.isAddressPublic),
          street: mapValue(args.address?.street),
          zip: mapValue(args.address?.zip),
        }
      : undefined,
    contact:
      args.contact?.phoneCountryCode && args.contact?.phoneNumber
        ? {
            isPhoneNumberPublic: mapValue(args.contact?.isPhoneNumberPublic),
            phoneCountryCode: mapValue(args.contact?.phoneCountryCode),
            phoneNumber: mapValue(args.contact?.phoneNumber),
          }
        : undefined,
    fitnessProfile: args?.fitnessProfile
      ? {
          bmi: mapValue(args.fitnessProfile?.bmi),
          bodyFat: mapValue(args.fitnessProfile?.bodyFat),
          bodyFatPercentage: mapValue(args.fitnessProfile?.bodyFatPercentage),
          fitnessGoals: mapValue(args?.fitnessProfile?.fitnessGoals)
            ?.flatMap((issue) =>
              issue?.checked && issue?.value ? issue.value : [],
            )
            .filter(Boolean) as string[],
          fitnessLevel,
          healthIssues: mapValue(args?.fitnessProfile?.healthIssues)
            ?.flatMap((issue) =>
              issue?.checked && issue?.value ? issue.value : [],
            )
            .filter(Boolean) as string[],
          heart: mapValue(args.fitnessProfile?.heart),
          height: mapValue(args.fitnessProfile?.height),
          maxHeartRate: mapValue(args.fitnessProfile?.maxHeartRate),
          muscleMass: mapValue(args.fitnessProfile?.muscleMass),
          muscleMassPercentage: mapValue(
            args.fitnessProfile?.muscleMassPercentage,
          ),
          weight: mapValue(args.fitnessProfile?.weight),
        }
      : undefined,
    name: mapValue(args?.name),
    username: mapValue(args?.username),
    socialMedia: args?.socialMedia
      ? mapValue(mergeDefaultSocialMedia(args.socialMedia))
      : undefined,
    userSettings: args.userSettings
      ? {
          bodyMassIndex: mapValue(args.userSettings?.bodyMassIndex?.[0]?.value),
          // Civil day end-to-end: the UI domain already carries
          // 'yyyy-MM-dd' (FormCivilDatePicker), so this is a validated
          // passthrough. `null` is omitted: the BE DTO is `.optional()` (no
          // clear-DOB contract; both DOB forms require a value), and the
          // legacy coercion turned null into 1970-01-01.
          dateOfBirth: isCivilDate(args.userSettings?.dateOfBirth)
            ? args.userSettings.dateOfBirth
            : undefined,
          energy: mapValue(args.userSettings?.energy?.[0]?.value),
          gender: mapValue(args.userSettings?.gender?.[0]?.value),
          height: mapValue(args.userSettings?.height?.[0]?.value),
          isPublic: mapValue(args.userSettings?.isPublic),
          length: mapValue(args.userSettings?.length?.[0]?.value),
          locale: mapValue(args.userSettings?.locale?.[0]?.value),
          privacyPolicyAccepted: mapValue(
            args.userSettings?.privacyPolicyAccepted,
          ),
          profileUrl: mapValue(args.userSettings?.profileUrl),
          rememberMe: mapValue(args.userSettings?.rememberMe),
          timezone: mapValue(args.userSettings?.timezone?.[0]?.value),
          volume: mapValue(args.userSettings?.volume?.[0]?.value),
          weight: mapValue(args.userSettings?.weight?.[0]?.value),
          dailyActivityReminderMorningTime: mapValue(
            args.userSettings?.dailyActivityReminderMorningTime,
          ),
          dailyActivityReminderEveningTime: mapValue(
            args.userSettings?.dailyActivityReminderEveningTime,
          ),
        }
      : undefined,
  } satisfies ApiUserUpdateArgs;
}

export function mapUserFromApi(apiUser: Maybe<ApiUser>): Maybe<UiUser> {
  const primaryAddress = pickPrimary(apiUser?.addresses);
  const primaryContact = pickPrimary(apiUser?.contacts);
  return {
    id: apiUser?.id ?? '',
    address: primaryAddress
      ? {
          city: mapValue(primaryAddress?.city),
          country: mapUserAddressCountryFromApi(primaryAddress?.country),
          isAddressPublic: mapValue(primaryAddress?.isAddressPublic),
          state: mapValue(primaryAddress?.state),
          street: mapValue(primaryAddress?.street),
          zip: mapValue(primaryAddress?.zip),
        }
      : undefined,
    contact: primaryContact
      ? {
          phoneCountryCode:
            mapValue(primaryContact?.phoneCountryCode) ??
            translationsConfig.defaultCountryCode,
          phoneNumber: mapValue(primaryContact?.phoneNumber) ?? '',
        }
      : undefined,
    createdAt: mapValue(apiUser?.createdAt),
    email: mapValue(apiUser?.email),
    fitnessProfile: mapFitnessProfileFromApi(apiUser?.fitnessProfiles),
    name: mapValue(apiUser?.name),
    username: mapValue(apiUser?.username),
    organizationUsers: mapOrganizationUsersFromApi(apiUser?.organizationUsers),
    personalTrainerOrganizationId: undefined,
    profileUrl: undefined,
    roles: mapUserRolesFromApi(mapValue(apiUser?.roles)) ?? [],
    settings: apiUser?.settings
      ? mapUserSettingsFromApi(apiUser?.settings)
      : undefined,
    socialMedia: apiUser?.socialMedia
      ? mergeDefaultSocialMedia(apiUser?.socialMedia)
      : undefined,
    status: apiUser?.organizationUsers?.[0]
      ? mapValue(apiUser?.organizationUsers?.[0]?.status)
      : mapValue(apiUser?.status),
    trainers:
      apiUser?.trainers?.map((trainer) => ({
        id: trainer.id,
        name: mapValue(trainer.name),
        settings: trainer.settings
          ? { profileUrl: mapValue(trainer.settings.profileUrl) }
          : undefined,
      })) ?? [],
  };
}
export function mapAuthorFromApi(
  apiUser: Maybe<ApiAuthor>,
): Maybe<UiAuthorUser> {
  const primaryAddress = pickPrimary(apiUser?.addresses);
  const primaryContact = pickPrimary(apiUser?.contacts);
  return {
    id: apiUser?.id ?? '',
    address: primaryAddress
      ? {
          city: mapValue(primaryAddress?.city),
          country: mapUserAddressCountryFromApi(primaryAddress?.country),
          isAddressPublic: mapValue(primaryAddress?.isAddressPublic),
          state: mapValue(primaryAddress?.state),
          street: mapValue(primaryAddress?.street),
          zip: mapValue(primaryAddress?.zip),
        }
      : undefined,
    contact: primaryContact
      ? {
          phoneCountryCode:
            mapValue(primaryContact?.phoneCountryCode) ??
            translationsConfig.defaultCountryCode,
          phoneNumber: mapValue(primaryContact?.phoneNumber) ?? '',
        }
      : undefined,
    createdAt: mapValue(apiUser?.createdAt),
    email: mapValue(apiUser?.email),
    fitnessProfile: mapFitnessProfileFromApi(apiUser?.fitnessProfiles),
    isPersonalTrainer: !!mapValue(apiUser?.isPersonalTrainer),
    name: mapValue(apiUser?.name),
    organizationUsers: undefined,
    personalTrainerOrganizationId: mapValue(
      apiUser?.personalTrainerOrganizationId,
    ),
    profileUrl: mapValue(apiUser?.settings?.profileUrl),
    roles: mapUserRolesFromApi(mapValue(apiUser?.roles)) ?? [],
    settings: apiUser?.settings
      ? mapUserSettingsFromApi(apiUser?.settings)
      : undefined,
    socialMedia: apiUser?.socialMedia
      ? mergeDefaultSocialMedia(apiUser?.socialMedia)
      : undefined,
    status: mapValue(apiUser?.status),
    trainers: undefined,
  };
}
export function mapAuthUserFromApi(
  apiUser: Maybe<ApiAuthUser>,
): Maybe<UiAuthUser> {
  const userPermissionsWithGroups = apiUser?.permissions
    ?.map((permission) => {
      // F1 (platform-user-class): platform users hold EVERY backend key.
      // A key absent from the FE role-editing catalog must not be silently
      // dropped — hasPermission() would lie about a permission the server
      // actually granted. Unknown keys keep the raw key under 'other';
      // PermissionsList intersects with the static catalog, so they never
      // leak into the role-editing UI.
      return (
        Permissions.find((p) => p.permission === permission) ??
        ({ group: 'other', permission } as UiPermission)
      );
    })
    .filter(Boolean) as UiPermission[];

  const primaryUserAddress = pickPrimary(apiUser?.user?.addresses);
  const primaryUserContact = pickPrimary(apiUser?.user?.contacts);

  return {
    ...mapUserFromApi(apiUser?.user),
    id: apiUser?.user?.id ?? '',
    address: primaryUserAddress
      ? {
          city: mapValue(primaryUserAddress?.city),
          country: mapUserAddressCountryFromApi(primaryUserAddress?.country),
          isAddressPublic: mapValue(primaryUserAddress?.isAddressPublic),
          state: mapValue(primaryUserAddress?.state),
          street: mapValue(primaryUserAddress?.street),
          zip: mapValue(primaryUserAddress?.zip),
        }
      : undefined,
    contact: primaryUserContact
      ? {
          isPhoneNumberPublic:
            mapValue(primaryUserContact?.isPhoneNumberPublic) ?? false,
          phoneCountryCode:
            primaryUserContact?.phoneCountryCode ??
            translationsConfig.defaultCountryCode,
          phoneNumber: mapValue(primaryUserContact?.phoneNumber) ?? '',
        }
      : undefined,
    corpOrganization: mapValue(apiUser?.user?.corpOrganization),
    corpOrganizationId: mapValue(apiUser?.user?.corpOrganizationId),
    createdAt: mapValue(apiUser?.user?.createdAt),
    email: mapValue(apiUser?.user?.email),
    fitnessProfile: mapFitnessProfileFromApi(apiUser?.user?.fitnessProfiles),
    hasFinishedOnboarding: isDefined(apiUser?.user?.onboardingCompletedAt),
    isPersonalTrainer: mapValue(apiUser?.isPersonalTrainer),
    isPlatformUser: mapValue(apiUser?.isPlatformUser),
    isSuperAdmin: mapValue(apiUser?.isSuperAdmin),
    membershipCardBillingEnabled: mapValue(
      apiUser?.membershipCardBillingEnabled,
    ),
    isPremium: mapValue(apiUser?.isPremium),
    lastUsedContext: mapValue(apiUser?.user?.lastUsedContext),
    lastUsedOrganization: apiUser?.lastUsedOrganization
      ? mapValue(mapOrganizationFromApi(apiUser?.lastUsedOrganization))
      : undefined,
    mustChangePassword: mapValue(apiUser?.mustChangePassword),
    name: mapValue(apiUser?.user?.name),
    organizationUsers: mapOrganizationUsersFromApi(
      apiUser?.user?.organizationUsers,
    ),
    owningOrganization: mapValue(apiUser?.owningOrganization),
    permissions: apiUser?.permissions ? userPermissionsWithGroups : [],
    personalTrainerOrganizationId: undefined,
    profileUrl: undefined,
    roles: mapUserRolesFromApi(mapValue(apiUser?.user?.roles)) ?? [],
    settings: apiUser?.user?.settings
      ? mapUserSettingsFromApi(apiUser?.user?.settings)
      : undefined,
    socialMedia: apiUser?.user?.socialMedia
      ? mergeDefaultSocialMedia(apiUser?.user?.socialMedia)
      : undefined,
    status: mapValue(apiUser?.user?.status),
    tenant: apiUser?.tenant,
    tenantId: mapValue(apiUser?.tenantId),
    trainers:
      apiUser?.user?.trainers?.map((trainer) => ({
        id: trainer.id,
        name: mapValue(trainer.name),
        settings: trainer.settings
          ? { profileUrl: mapValue(trainer.settings.profileUrl) }
          : undefined,
      })) ?? [],
    type: mapValue(apiUser?.type),
  };
}
